import useSWR, { mutate as mutateStatic } from 'swr';
import { fuego } from '../context/Provider';
import { useRef, useEffect, useMemo, useCallback } from 'react';
import { empty } from '../helpers/empty';
import { collectionCache } from '../classes/Cache';
import { query, getDocs as _getDocs, onSnapshot, collection, collectionGroup, orderBy as _orderBy, where as _where, startAt as _startAt, startAfter as _startAfter, endBefore as _endBefore, endAt as _endAt, limit as _limit, doc as _doc, writeBatch } from '@firebase/firestore';
import { isDev } from '../helpers/is-dev';
import { withDocumentDatesParsed } from '../helpers/doc-date-parser';
export const getDocs = async function (path) {
  let query = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let {
    parseDates,
    ignoreFirestoreDocumentSnapshotField
  } = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : empty.object;
  const ref = createFirestoreRef(path, query);
  const data = await _getDocs(ref).then(querySnapshot => {
    const array = [];
    querySnapshot.forEach(doc => {
      const docData = doc.data({
        serverTimestamps: 'estimate'
      }) ?? empty.object;
      const docToAdd = withDocumentDatesParsed({ ...docData,
        id: doc.id,
        exists: doc.exists(),
        hasPendingWrites: doc.metadata.hasPendingWrites,
        __snapshot: ignoreFirestoreDocumentSnapshotField ? undefined : doc
      }, parseDates); // update individual docs in the cache

      mutateStatic(doc.ref.path, docToAdd, false);

      if (isDev && (docData.exists || docData.id || docData.hasPendingWrites)) {
        console.warn('[get-collection] warning: Your document, ', doc.id, ' is using one of the following reserved fields: [exists, id, hasPendingWrites]. These fields are reserved. Please remove them from your documents.');
      }

      array.push(docToAdd);
    });
    return array;
  });
  return data;
};

const createFirestoreRef = (path, _ref) => {
  let {
    where,
    orderBy,
    limit,
    startAt,
    endAt,
    startAfter,
    endBefore,
    isCollectionGroup
  } = _ref;
  let ref = collection(fuego.db, path);
  const queries = [];

  if (isCollectionGroup) {
    ref = collectionGroup(fuego.db, path);
  }

  if (where) {
    const multipleConditions = w => {
      return !!w && Array.isArray(w[0]);
    };

    (multipleConditions(where) ? where : [where]).forEach(w => queries.push(_where(w[0], w[1], w[2])));
  }

  if (orderBy) {
    if (typeof orderBy === 'string') {
      queries.push(_orderBy(orderBy));
    } else if (Array.isArray(orderBy)) {
      const multipleOrderBy = o => {
        return Array.isArray(o[0]);
      };

      (multipleOrderBy(orderBy) ? orderBy : [orderBy]).forEach(_ref2 => {
        let [order, direction] = _ref2;
        return queries.push(_orderBy(order, direction));
      });
    }
  }

  if (startAt) {
    queries.push(_startAt(startAt));
  }

  if (endAt) {
    queries.push(_endAt(endAt));
  }

  if (startAfter) {
    queries.push(_startAfter(startAfter));
  }

  if (endBefore) {
    queries.push(_endBefore(endBefore));
  }

  if (limit) {
    queries.push(_limit(limit));
  }

  return queries.length > 0 ? query(ref, ...queries) : ref;
};

const createListenerAsync = async (path, queryString, _ref3) => {
  let {
    parseDates,
    ignoreFirestoreDocumentSnapshotField = true
  } = _ref3;
  return new Promise(resolve => {
    const query = JSON.parse(queryString) ?? {};
    const ref = createFirestoreRef(path, query);
    const unsubscribe = onSnapshot(ref, {
      includeMetadataChanges: true
    }, querySnapshot => {
      const data = [];
      querySnapshot.forEach(doc => {
        const docData = doc.data({
          serverTimestamps: 'estimate'
        }) ?? empty.object;
        const docToAdd = withDocumentDatesParsed({ ...docData,
          id: doc.id,
          exists: doc.exists(),
          hasPendingWrites: doc.metadata.hasPendingWrites,
          __snapshot: ignoreFirestoreDocumentSnapshotField ? undefined : doc
        }, parseDates);

        if (isDev && (docData.exists || docData.id || docData.hasPendingWrites)) {
          console.warn('[use-collection] warning: Your document, ', doc.id, ' is using one of the following reserved fields: [exists, id, hasPendingWrites]. These fields are reserved. Please remove them from your documents.');
        } // update individual docs in the cache


        mutateStatic(doc.ref.path, docToAdd, false);
        data.push(docToAdd);
      }); // resolve initial data

      resolve({
        initialData: data,
        unsubscribe
      }); // update on listener fire

      mutateStatic([path, queryString], data, false);
    });
  });
};

/**
 * Call a Firestore Collection
 * @template Doc
 * @param path String if the document is ready. If it's not ready yet, pass `null`, and the request won't start yet.
 * @param [query] - Dictionary with options to query the collection *AND* optionally accepts `listen`, `parseDates`, and `ignoreFirestoreDocumentSnapshotField` as well.
 * @param [options] - Dictionary of options to pass to the underlying useSWR library.
 */
export const useCollection = function (path) {
  let query = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : empty.object;
  let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : empty.object;
  const unsubscribeRef = useRef(null);
  const {
    where,
    endAt,
    endBefore,
    startAfter,
    startAt,
    orderBy,
    limit,
    listen = false,
    parseDates,
    // __unstableCollectionGroup: isCollectionGroup = false,
    isCollectionGroup,
    ignoreFirestoreDocumentSnapshotField = true
  } = query; // if we're listening, the firestore listener handles all revalidation

  const {
    refreshInterval = listen ? 0 : undefined,
    refreshWhenHidden = listen ? false : undefined,
    refreshWhenOffline = listen ? false : undefined,
    revalidateOnFocus = listen ? false : undefined,
    revalidateOnReconnect = listen ? false : undefined,
    dedupingInterval = listen ? 0 : undefined
  } = options;
  const swrOptions = { ...options,
    refreshInterval,
    refreshWhenHidden,
    refreshWhenOffline,
    revalidateOnFocus,
    revalidateOnReconnect,
    dedupingInterval
  }; // why not just put this into the ref directly?
  // so that we can use the useEffect down below that triggers revalidate()

  const memoQueryString = useMemo(() => JSON.stringify({
    where,
    endAt,
    endBefore,
    startAfter,
    startAt,
    orderBy,
    limit,
    isCollectionGroup
  }), [endAt, endBefore, isCollectionGroup, limit, orderBy, startAfter, startAt, where]); // we move this to a Ref
  // why? because we shouldn't have to include it in the key
  // if we do, then calling mutate() won't be consistent for all
  // collections with the same path & query
  // TODO figure out if this is the right behavior...probably not because of the paths. hm.
  // TODO it's not, move this to the
  // const isCollectionGroupQuery = useRef(isCollectionGroup)
  // useEffect(() => {
  //   isCollectionGroupQuery.current = isCollectionGroup
  // }, [isCollectionGroup])

  const dateParser = useRef(parseDates);
  useEffect(() => {
    dateParser.current = parseDates;
  }, [parseDates]); // we move listen to a Ref
  // why? because we shouldn't have to include "listen" in the key
  // if we do, then calling mutate() won't be consistent for all
  // collections with the same path & query

  const shouldListen = useRef(listen);
  useEffect(() => {
    shouldListen.current = listen;
  });
  const shouldIgnoreSnapshot = useRef(ignoreFirestoreDocumentSnapshotField);
  useEffect(() => {
    shouldIgnoreSnapshot.current = ignoreFirestoreDocumentSnapshotField;
  }, [ignoreFirestoreDocumentSnapshotField]);
  const swr = useSWR( // if the path is null, this means we don't want to fetch yet.
  path === null ? null : [path, memoQueryString], async (path, queryString) => {
    if (shouldListen.current) {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      const {
        unsubscribe,
        initialData
      } = await createListenerAsync(path, queryString, {
        parseDates: dateParser.current,
        ignoreFirestoreDocumentSnapshotField: shouldIgnoreSnapshot.current
      });
      unsubscribeRef.current = unsubscribe;
      return initialData;
    }

    const data = await getDocs(path, JSON.parse(queryString), {
      parseDates: dateParser.current,
      ignoreFirestoreDocumentSnapshotField: shouldIgnoreSnapshot.current
    });
    return data;
  }, swrOptions); // if listen or changes,
  // we run revalidate.
  // This triggers SWR to fetch again
  // Why? because we don't want to put listen
  // in the useSWR key. If we did, then we couldn't mutate
  // based on query alone. If we had useSWR(['users', true]),
  // but then a `users` fetch with `listen` set to `false` updated, it wouldn't mutate both.
  // thus, we move the `listen` and option to a ref user in `useSWR`,
  // and we call `revalidate` if it changes.

  const mounted = useRef(false);
  useEffect(() => {
    // TODO should this only happen if listen is false? No, BC swr should revalidate on a change.
    if (mounted.current) revalidateRef.current();else mounted.current = true;
  }, [listen]); // this MUST be after the previous effect to avoid duplicate initial validations.
  // only happens on updates, not initial mounting

  const revalidateRef = useRef(swr.mutate);
  useEffect(() => {
    revalidateRef.current = swr.mutate;
  });
  useEffect(() => {
    // TODO should this only be for listen, since SWR updates with the others?
    // also should it go before the useSWR?
    return () => {
      // clean up listener on unmount if it exists
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    }; // should depend on the path, queyr, and listen being the same...
  }, [path, listen, memoQueryString]); // add the collection to the cache,
  // so that we can mutate it from document calls later

  useEffect(() => {
    if (path) collectionCache.addCollectionToCache(path, memoQueryString);
  }, [path, memoQueryString]);
  const {
    data,
    isValidating,
    mutate,
    error
  } = swr;
  /**
   * `add(data)`: Extends the Firestore document [`add` function](https://firebase.google.com/docs/firestore/manage-data/add-data).
   * - It also updates the local cache using SWR's `mutate`. This will prove highly convenient over the regular `add` function provided by Firestore.
   */

  const add = useCallback(data => {
    if (!path) return null;
    const multiple = Array.isArray(data);
    const dataArray = multiple ? data : [data];
    const ref = collection(fuego.db, path);
    const docsToAdd = dataArray.map(doc => ({ ...doc,
      // generate IDs we can use that in the local cache that match the server
      id: _doc(ref).id
    })); // solve this annoying TS bug 😅
    // add to cache

    if (!listen) {
      // we only update the local cache if we don't have a listener set up
      // why? because Firestore automatically handles this part for subscriptions
      mutate(prevState => {
        const state = prevState ?? empty.array;
        return [...state, ...docsToAdd];
      }, false);
    } // add to network


    const batch = writeBatch(fuego.db);
    docsToAdd.forEach(_ref4 => {
      let {
        id,
        ...doc
      } = _ref4;
      // take the ID out of the document
      batch.set(_doc(ref, id), doc);
    });
    return batch.commit().then(() => {
      const ids = docsToAdd.map(_ref5 => {
        let {
          id
        } = _ref5;
        return id;
      });
      const returnValue = multiple ? ids : ids[0];
      return returnValue;
    });
  }, [listen, mutate, path]);
  return {
    data,
    isValidating,
    mutate,
    error,
    add,
    loading: !data && !error,

    /**
     * A function that, when called, unsubscribes the Firestore listener.
     *
     * The function can be null, so make sure to check that it exists before calling it.
     *
     * Note: This is not necessary to use. `useCollection` already unmounts the listener for you. This is only intended if you want to unsubscribe on your own.
     */
    unsubscribe: unsubscribeRef.current
  };
};
//# sourceMappingURL=use-swr-collection.js.map